/**
 * The peon talks in notifications (owner, 2026-10-04: "There should be notification sounds which
 * are the peon talking as well", "There should be one for gold rush"). Each kind of push names a
 * sound; the iOS app carries it as a bundled .caf (client/assets/sounds, made by
 * scripts/make-notification-sounds.mjs from these lines) that Apple plays with the app closed,
 * and an open client (web or app) plays the same line itself when the push arrives. Web Push
 * cannot carry a sound, so a closed browser gets the phone's default tone.
 *
 * Pure data, shared by the server (server/push.ts picks the sound), the clients (which line to
 * play) and the asset script (which file to render). See docs/timed-notes.md.
 */

/** A notification sound's name: the .caf in the app bundle is `${name}.caf`. */
export type PushSound = "peon-new-task" | "peon-sent" | "peon-done" | "peon-note" | "peon-urgent" | "peon-overdue" | "peon-rush" | "peon-reward" | "peon-psst" | "peon-ring" | "peon-badge" | "peon-rank";

/** Sound name -> the original peon line (public/audio/peons-original, without .wav) it plays. */
export const PUSH_SOUND_LINES: Record<Exclude<PushSound, "peon-ring">, string> = {
  "peon-new-task": "added-new-job", // "New job. Great."
  "peon-sent": "notify-someone", // "Hrrm? Someone want you."
  "peon-done": "task-done", // "There. All done."
  "peon-note": "note-remember", // "Me remember. Maybe."
  "peon-urgent": "urgent-now-now", // "Now? Now now?"
  "peon-overdue": "done-yet", // "Is it done yet? No? Okay."
  "peon-rush": "rush-gold-quick", // "Gold! Quick, quick!"
  "peon-reward": "reward-shiny", // "Ooh. Shiny."
  "peon-psst": "notify-psst", // "Psst. Something happen." (anything else)
  "peon-badge": "badge-for-me", // "Badge? For me? Hrrm. Nice." (docs/achievements.md)
  "peon-rank": "rank-big-peon", // "New rank. Me big peon now."
};

/**
 * The timed note's ring (peon-ring.caf): the chime from public/audio/ui/alarm.wav with the two
 * timer lines between its bars. An open client plays the chime in a loop and says these first.
 */
export const RING_LINES = ["timer-ding", "timer-time"] as const; // "Ding. Thing is ready. Go look.", "Time! Me think. Yes. Time."

export const PUSH_SOUNDS: readonly PushSound[] = [...(Object.keys(PUSH_SOUND_LINES) as PushSound[]), "peon-ring"];

/** The sound a push carries when its builder names none. */
export const DEFAULT_PUSH_SOUND: PushSound = "peon-psst";

export function isPushSound(s: unknown): s is PushSound {
  return typeof s === "string" && (PUSH_SOUNDS as readonly string[]).includes(s);
}
